import React, { useState } from 'react';
import { Box, Typography, Paper, Accordion, AccordionSummary, AccordionDetails, Chip, TextField, InputAdornment } from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  Search as SearchIcon,
  Dashboard as BoardIcon,
  Payments as PayoutIcon,
  AccountBalanceWallet as AccountingIcon,
  Toll as CreditsIcon,
  SportsFootball as GameIcon,
  People as PlayersIcon,
  Settings as SettingsIcon,
  Palette as ThemesIcon,
} from '@mui/icons-material';
import { SupportLayout } from '../../components/support';
import { useDocumentTitle } from '../../hooks/core';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const faqData: FAQItem[] = [
  // Boards
  {
    category: 'Boards',
    question: 'How many squares are on a board?',
    answer: 'SquareUp boards are arranged in a grid where each row and column is assigned a digit from 0-9. Standard boards have 100 squares (10x10), with additional board sizes coming soon.',
  },
  {
    category: 'Boards',
    question: 'Can I have multiple boards at the same time?',
    answer: 'Yes! You can create and manage as many boards as you have credits for. Each board operates independently.',
  },
  {
    category: 'Boards',
    question: 'What happens if I delete a board?',
    answer: 'Deleting a board permanently removes it and all associated data. Credits used for boards that have been published (went live) are not refunded. Credits for boards deleted while still in draft status are returned.',
  },
  {
    category: 'Boards',
    question: 'Can I edit a board after it\'s live?',
    answer: 'You can edit most settings while a board is live, including the name, description, and some payout settings. However, you cannot change the price per square once players have joined.',
  },
  {
    category: 'Boards',
    question: 'What is the invite code?',
    answer: 'The invite code is a unique 6-character code that players use to join your board. Share it via text, email, or any messaging app. Players enter this code when clicking "Join Board."',
  },
  {
    category: 'Boards',
    question: 'Can I add a co-manager to my board?',
    answer: 'Yes! Go to board settings and add another user as a manager. Co-managers can perform most actions except deleting the board or removing the owner.',
  },
  {
    category: 'Boards',
    question: 'How do I share my board with others?',
    answer: 'Once your board is published (live), you can share it using the shareable link found in the Share panel on your board. Anyone with the link can view the board, see claimed squares, and follow along during the game—no account required.',
  },
  {
    category: 'Boards',
    question: 'What can people see on the public board view?',
    answer: 'The public view shows the board grid with claimed squares, team names, payout information, and the game log with recorded scores. Viewers can see who owns each square (by display name or handle) and which squares have won. They cannot make changes or see private accounting details.',
  },
  {
    category: 'Boards',
    question: 'Why does my shared link show "Board Coming Soon"?',
    answer: 'The public view is only available after you publish your board. While in draft status, visitors will see a "Coming Soon" message. Publish your board from the board settings when you\'re ready for people to view it.',
  },
  {
    category: 'Boards',
    question: 'Can I share the board before the numbers are revealed?',
    answer: 'Yes! You can share the link at any time after publishing. If numbers haven\'t been randomized yet, the row and column headers will show dashes instead of the actual numbers.',
  },

  // Game Mechanics
  {
    category: 'Game',
    question: 'How is the winning square determined?',
    answer: 'The winning square is found by taking the last digit of each team\'s score. The row corresponds to one team\'s last digit, and the column corresponds to the other team\'s. The intersection is the winning square.',
  },
  {
    category: 'Game',
    question: 'When should I randomize the numbers?',
    answer: 'Randomize numbers after all squares are claimed (or you\'re ready to close claiming) and before locking the board. Many organizers do this right before the game starts.',
  },
  {
    category: 'Game',
    question: 'Can the same square win multiple times?',
    answer: 'Yes! If the score\'s last digits match at multiple quarters, the same square wins each time. That lucky player gets payouts for each quarter they win.',
  },
  {
    category: 'Game',
    question: 'What if a game goes to overtime?',
    answer: 'By default, Q4/Final is based on the game\'s final score (including overtime). You can optionally configure overtime settings to pay separately for end of regulation and final score.',
  },
  {
    category: 'Game',
    question: 'Can I use SquareUp for basketball or other sports?',
    answer: 'Absolutely! SquareUp works with any sport that has numerical scores. You can configure 2, 3, or 4 scoring periods depending on your sport.',
  },

  // Payouts
  {
    category: 'Payouts',
    question: 'What is Right Way vs Wrong Way payout?',
    answer: 'Right Way is the main payout based on the actual score. Wrong Way (also called "reverse") pays a bonus to the square with the team digits swapped. If score is 17-14 (Right Way winner is row 4, col 7), the Wrong Way winner is row 7, col 4.',
  },
  {
    category: 'Payouts',
    question: 'What is the +5 (or +N) rule?',
    answer: 'The +N rule adds a fixed number to one team\'s last digit to find a secondary winner. With +5, if the away team\'s last digit is 4, the +5 winner uses row (4+5) mod 10 = 9 instead. You can enable +N separately for Right Way and Wrong Way payouts.',
  },
  {
    category: 'Payouts',
    question: 'Can I enable +N for both Right Way and Wrong Way?',
    answer: 'Yes! You can enable +N for Right Way, Wrong Way, or both independently. When both are enabled, you get up to four winners per quarter: Right Way, Wrong Way, Right Way +N, and Wrong Way +N.',
  },
  {
    category: 'Payouts',
    question: 'Can I take a profit as the organizer?',
    answer: 'Yes! You can set an organizer profit percentage (0-50%) in payout settings. This amount is deducted before calculating winner payouts.',
  },

  // Accounting
  {
    category: 'Accounting',
    question: 'How do I track who has paid?',
    answer: 'Use the Ledger tab on your board to see everyone\'s balance. When someone pays you (Venmo, cash, etc.), click "Record Payment" to add it to their ledger.',
  },
  {
    category: 'Accounting',
    question: 'What does a negative balance mean?',
    answer: 'A negative balance means the player owes you money. A positive balance means you owe them money (usually from winnings).',
  },
  {
    category: 'Accounting',
    question: 'How do I mark winnings as paid?',
    answer: 'Click on a player in the Ledger, then use "Mark Winnings Paid" or record a manual payment. This creates a ledger entry showing you\'ve paid out their earnings.',
  },
  {
    category: 'Accounting',
    question: 'Can I see accounting across all my boards?',
    answer: 'Yes! The main Accounting page (in navigation) shows aggregated balances across all your completed boards. Click any player to see their detailed history.',
  },

  // Players
  {
    category: 'Players',
    question: 'What is an offline player?',
    answer: 'Offline players are placeholders for people who don\'t want to create a SquareUp account. You manage their squares and payments for them. Perfect for family members or cash players.',
  },
  {
    category: 'Players',
    question: 'Can players pick which squares they want?',
    answer: 'Yes, when players join they can click to claim any available square. As the manager, you can also assign squares to players or offline players.',
  },
  {
    category: 'Players',
    question: 'What if someone needs more squares?',
    answer: 'Players can claim multiple squares (up to the limit you set). As manager, you can also assign additional squares to any player.',
  },
  {
    category: 'Players',
    question: 'Can I remove a player from my board?',
    answer: 'You can clear squares (remove ownership) from the board settings. The player remains in the participant list but won\'t own any squares.',
  },

  // Credits
  {
    category: 'Credits',
    question: 'How many free credits do I get?',
    answer: 'New accounts receive 2 free board credits. These work exactly like purchased credits with no limitations.',
  },
  {
    category: 'Credits',
    question: 'Do credits expire?',
    answer: 'No! Credits never expire. Buy them when you need them, use them whenever you want.',
  },
  {
    category: 'Credits',
    question: 'How does the referral program work?',
    answer: 'Share your unique referral link. When someone signs up and purchases credits, you both earn bonus credits. The more referrals you make, the higher your tier and rewards.',
  },
  {
    category: 'Credits',
    question: 'What payment methods are accepted?',
    answer: 'We accept all major credit/debit cards (Visa, Mastercard, Amex, Discover) plus Apple Pay and Google Pay through our secure Stripe integration.',
  },

  // Settings
  {
    category: 'Settings',
    question: 'Can I change my display name?',
    answer: 'Yes! Go to Profile > Settings to update your display name, handle (4-character code shown on squares), and avatar icon.',
  },
  {
    category: 'Settings',
    question: 'What are board templates?',
    answer: 'Templates save your board configuration (sport, quarters, price, payouts, and theme) so you can quickly create similar boards in the future. Create templates from Profile > Templates, set a default, and even import/export them.',
  },
  {
    category: 'Settings',
    question: 'Can I customize the board appearance?',
    answer: 'Yes! SquareUp offers themes that change colors, styling, and visual effects. Browse the Theme Store to find free and premium themes.',
  },
  {
    category: 'Settings',
    question: 'How do I change the home/away orientation?',
    answer: 'In board settings, toggle "Home on Top" to switch which team appears on the columns (top) vs rows (side).',
  },
  // Themes
  {
    category: 'Themes',
    question: 'What are themes?',
    answer: 'Themes control the visual appearance of your board—colors, backgrounds, borders, and styling. Choose from pre-made themes in the store.',
  },
  {
    category: 'Themes',
    question: 'Are purchased themes good for all my boards?',
    answer: 'Yes! Once you purchase a theme, it\'s added to your library and can be used on unlimited boards.',
  },
  {
    category: 'Themes',
    question: 'Can I preview a theme before buying?',
    answer: 'Yes! Click on any theme in the store to see a full preview on a sample board before purchasing.',
  },

  // Offline Players (expanded)
  {
    category: 'Players',
    question: 'Can I limit how many squares an offline player can claim?',
    answer: 'Yes! Set an "Approved Squares" limit on offline players to control how many squares they can have. This helps manage cash collection and ensures fair distribution.',
  },
  {
    category: 'Players',
    question: 'Can I set a starting balance for offline players?',
    answer: 'Yes! When creating an offline player, you can set a starting balance if they\'ve pre-paid (like cash upfront). This ensures accurate accounting from day one.',
  },
];

const categoryIcons: Record<string, React.ReactNode> = {
  Boards: <BoardIcon />,
  Game: <GameIcon />,
  Payouts: <PayoutIcon />,
  Accounting: <AccountingIcon />,
  Players: <PlayersIcon />,
  Credits: <CreditsIcon />,
  Settings: <SettingsIcon />,
  Themes: <ThemesIcon />,
};

const categoryColors: Record<string, string> = {
  Boards: '#2196f3',
  Game: '#9c27b0',
  Payouts: '#ff9800',
  Accounting: '#f44336',
  Players: '#4caf50',
  Credits: '#00bcd4',
  Settings: '#607d8b',
  Themes: '#e91e63',
};

export const FAQPage: React.FC = () => {
  useDocumentTitle('FAQ - Support');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategory, setExpandedCategory] = useState<string | false>('Boards');

  // Get unique categories
  const categories = [...new Set(faqData.map((faq) => faq.category))];

  // Filter FAQs based on search
  const filteredFAQs = searchQuery
    ? faqData.filter(
        (faq) =>
          faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : faqData;

  // Group by category
  const groupedFAQs = categories.reduce((acc, category) => {
    acc[category] = filteredFAQs.filter((faq) => faq.category === category);
    return acc;
  }, {} as Record<string, FAQItem[]>);

  return (
    <SupportLayout
      title="Frequently Asked Questions"
      description="Find answers to common questions about SquareUp, sports squares pools, payouts, and more."
      breadcrumbs={[{ label: 'FAQ' }]}
    >
      {/* Search */}
      <Paper elevation={0} sx={{ p: 3, mb: 4, bgcolor: 'grey.50' }}>
        <TextField
          fullWidth
          placeholder="Search questions..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ bgcolor: 'white' }}
        />
        {searchQuery && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Found {filteredFAQs.length} result{filteredFAQs.length !== 1 ? 's' : ''} for "{searchQuery}"
          </Typography>
        )}
      </Paper>

      {/* FAQ Categories */}
      {searchQuery ? (
        // Show flat list when searching
        <Box>
          {filteredFAQs.length === 0 ? (
            <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: 1, borderColor: 'divider' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No results found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try different keywords or browse categories below
              </Typography>
            </Paper>
          ) : (
            filteredFAQs.map((faq, index) => (
              <Accordion key={index} elevation={0} sx={{ border: 1, borderColor: 'divider', mb: 1, '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip
                      label={faq.category}
                      size="small"
                      icon={categoryIcons[faq.category] as React.ReactElement}
                      sx={{ bgcolor: `${categoryColors[faq.category]}20`, color: categoryColors[faq.category] }}
                    />
                    <Typography fontWeight="500">{faq.question}</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary">
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </Box>
      ) : (
        // Show grouped by category when not searching
        <Box>
          {categories.map((category) => (
            <Accordion
              key={category}
              expanded={expandedCategory === category}
              onChange={(_, isExpanded) => setExpandedCategory(isExpanded ? category : false)}
              elevation={0}
              sx={{
                border: 1,
                borderColor: 'divider',
                mb: 2,
                '&:before': { display: 'none' },
                borderRadius: '8px !important',
                overflow: 'hidden',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandIcon />}
                sx={{
                  bgcolor: expandedCategory === category ? `${categoryColors[category]}10` : 'transparent',
                  '&:hover': { bgcolor: `${categoryColors[category]}10` },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ color: categoryColors[category] }}>{categoryIcons[category]}</Box>
                  <Typography variant="h6" fontWeight="bold">
                    {category}
                  </Typography>
                  <Chip
                    label={groupedFAQs[category]?.length || 0}
                    size="small"
                    sx={{ bgcolor: 'grey.200' }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {groupedFAQs[category]?.map((faq, index) => (
                  <Accordion
                    key={index}
                    elevation={0}
                    sx={{
                      '&:before': { display: 'none' },
                      borderTop: index > 0 ? 1 : 0,
                      borderColor: 'divider',
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandIcon />} sx={{ pl: 4 }}>
                      <Typography fontWeight="500">{faq.question}</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pl: 4, bgcolor: 'grey.50' }}>
                      <Typography variant="body2" color="text.secondary">
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* Still need help */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          mt: 4,
          textAlign: 'center',
          bgcolor: 'primary.50',
          border: 1,
          borderColor: 'primary.200',
        }}
      >
        <Typography variant="h6" gutterBottom>
          Still have questions?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Check out our detailed guides in the Support Center navigation, or browse the specific topic pages for more in-depth information.
        </Typography>
      </Paper>
    </SupportLayout>
  );
};

export default FAQPage;
